/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ScaleLog, ScaleLinear } from '#utils/PRIVATE_chartUtils';
import { DataAxisInfoProps } from '#utils/UNSAFE_visTypes/chart';

const MAX_NUMBER_OF_GRIDS_AUTO = 10;
const MINOR_TICK_COUNT = 2;
const MAJOR_TICK_INCREMENT_BUFFER = 0.0000000001;
const MAX_ZOOM = 64;

export class DataAxisInfo {
  numGroups!: number;
  dataMin!: number;
  dataMax!: number;
  viewportMin?: number;
  viewportMax?: number;
  min?: number;
  max?: number;
  isLog: boolean;
  majorTickCount!: number;
  minorTickCount!: number;
  majorIncrement?: number;
  minorIncrement?: number;
  minMajorIncrement?: number;
  isZeroBaseline: boolean;

  constructor({
    scale = 'linear',
    baselineScaling = 'zero',
    dataMax,
    dataMin,
    min,
    max,
    viewportMin,
    viewportMax,
    numGroups,
    ...options
  }: DataAxisInfoProps & { numGroups: number, majorTickCount?: number }) {
    this.numGroups = numGroups;
    this.isLog = scale === 'log';
    this.isZeroBaseline = !this.isLog && baselineScaling === 'zero';

    this.setDataRange(dataMin!, dataMax!);
    this.setViewportRange(viewportMin, viewportMax, min, max);

    this.majorIncrement = this.actualToLinear(options.step);
    this.minorIncrement = this.actualToLinear(options.minorStep);
    this.minMajorIncrement = this.actualToLinear(options.minStep);
    this.majorTickCount = options.majorTickCount!;

    this.calcAxisExtents();
  }

  getInfo() {
    return {
      min: this.min!,
      max: this.max!,
      viewportMin: this.viewportMin!,
      viewportMax: this.viewportMax!,
      viewportStartGroup: undefined,
      viewportEndGroup: undefined,
      startIndex: 0,
      endIndex: this.numGroups - 1,
      ticks: this.getTicks(),
      minorTicks: this.getMinorTicks(),
      minAxisExtent: this.getMinExtent(),
      getScale: this.getScale.bind(this),
      step: this.majorIncrement!
    };
  }

  /**
   * Set the dataMin and dataMax.
   * @param dataMin The minimum of the data.
   * @param dataMax The maximum of the data.
   */
  setDataRange(dataMin: number, dataMax: number) {
    this.dataMin = this.actualToLinear(dataMin)!;
    this.dataMax = this.actualToLinear(dataMax)!;

    if (this.isZeroBaseline) {
      this.dataMin = Math.min(0, this.dataMin);
      this.dataMax = Math.max(0, this.dataMax!);
    }
  }

  setViewportRange(viewportMin?: number, viewportMax?: number, min?: number, max?: number) {
    this.min = this.actualToLinear(min);
    this.max = this.actualToLinear(max);
    this.viewportMin = viewportMin == null ? this.min : this.actualToLinear(viewportMin);
    this.viewportMax = viewportMax == null ? this.max : this.actualToLinear(viewportMax);
  }

  setDefaultMin(scaleUnit: number) {
    // if already set in setViewportRange
    if (this.min != null) {
      return;
    }

    if (this.isZeroBaseline && this.dataMin >= 0) {
      this.min = 0;
    } else if (!this.isZeroBaseline && this.max != null) {
      this.min = this.max - scaleUnit * (Math.floor((this.max - this.dataMin) / scaleUnit) + 1);
    } else {
      this.min = (Math.ceil(this.dataMin / scaleUnit) - 1) * scaleUnit;
    }

    // If all data points are positive, the axis min shouldn't be less than zero
    if (this.dataMin >= 0 && !this.isLog) {
      this.min = Math.max(this.min, 0);
    }
  }

  setDefaultMax(scaleUnit: number, minValue: number, maxValue: number) {
    if (this.max != null) {
      return;
    }
    if (this.majorTickCount) {
      this.max = this.min! + this.majorTickCount * scaleUnit;

      if (this.max < this.dataMax) {
        const axisStep = this.calcAxisStep(minValue, maxValue + scaleUnit);
        scaleUnit = this.minMajorIncrement ? Math.max(axisStep, this.minMajorIncrement) : axisStep;
        this.max = this.min! + this.majorTickCount * scaleUnit;
      }
    } else if (this.isZeroBaseline && this.dataMax <= 0) {
      this.max = 0;
    } else if (!this.isZeroBaseline && this.min != null) {
      this.max = this.min + scaleUnit * (Math.floor((this.dataMax - this.min) / scaleUnit) + 1);
    } else {
      this.max = (Math.floor(this.dataMax / scaleUnit) + 1) * scaleUnit;
    }
    // If all data points are negative, the axis max shouldn't be more that zero
    if (this.dataMax <= 0) {
      this.max = Math.min(this.max, 0);
    }
  }

  calcAxisExtents() {
    // Include 0 in the axis if we're scaling from the baseline
    if (this.isZeroBaseline) {
      this.dataMin = Math.min(0, this.dataMin);
      this.dataMax = Math.max(0, this.dataMax);
    }

    const maxValue = this.max != null ? this.max : this.dataMax;
    const minValue = this.min != null ? this.min : this.dataMin;
    const axisStep = this.calcAxisStep(minValue, maxValue);
    let scaleUnit = this.minMajorIncrement ? Math.max(axisStep, this.minMajorIncrement) : axisStep;

    // If there's only a single value on the axis, we need to adjust the
    // this.dataMin and this.dataMax to produce a nice looking axis with around 6 ticks.
    if (this.dataMin === this.dataMax) {
      if (this.dataMin === 0) {
        this.dataMax += 5 * scaleUnit;
      } else {
        this.dataMin -= 2 * scaleUnit;
        this.dataMax += 2 * scaleUnit;
      }
    }

    this.setDefaultMin(scaleUnit);

    this.setDefaultMax(scaleUnit, minValue, maxValue);

    if (this.max === this.min) {
      // happens if this.dataMin === this.dataMax === 0
      this.max = 100;
      this.min = 0;
      scaleUnit = (this.max - this.min) / MAX_NUMBER_OF_GRIDS_AUTO;
    }

    if (
      (this.viewportMin !== this.min || this.viewportMax !== this.max) &&
      this.viewportMax != null &&
      this.viewportMin != null
    ) {
      scaleUnit = this.calcAxisStep(this.viewportMin, this.viewportMax);
    }

    if (this.viewportMin == null) {
      this.viewportMin = this.min;
    }

    if (this.viewportMax == null) {
      this.viewportMax = this.max;
    }

    this.calcMajorMinorSteps(scaleUnit);
  }

  /**
   * Determines the number of major and minor tick counts and increments for the axis if values were not given.
   * The default minor tick count is 2.
   * @param {number} scaleUnit The scale unit of the axis.
   * @private
   */

  calcMajorMinorSteps(scaleUnit: number) {
    if (this.max == null || this.min == null) {
      return;
    }
    if (!this.majorIncrement) {
      if (this.majorTickCount) {
        this.majorIncrement = (this.viewportMax! - this.viewportMin!) / this.majorTickCount;
      } else {
        this.majorIncrement = this.minMajorIncrement
          ? Math.max(scaleUnit, this.minMajorIncrement!)
          : scaleUnit;
      }
    }

    if (!this.majorTickCount) {
      this.majorTickCount = (this.viewportMax! - this.viewportMin!) / this.majorIncrement;

      // Check if we have a floating point inaccuracy that causes the tick count to be undercalculated
      // within the allowable buffer. If so, tick count is supposed to be the rounded up integer.
      if (Math.ceil(this.majorTickCount) - this.majorTickCount < MAJOR_TICK_INCREMENT_BUFFER) {
        this.majorTickCount = Math.ceil(this.majorTickCount);
      }
    }

    if (!this.minorTickCount) {
      if (this.minorIncrement) {
        this.minorTickCount = this.majorIncrement / this.minorIncrement;
      } else if (this.isLog) {
        this.minorTickCount = this.majorIncrement;
      } else {
        this.minorTickCount = MINOR_TICK_COUNT;
      }
    }

    if (!this.minorIncrement) {
      this.minorIncrement = this.majorIncrement / this.minorTickCount;
    }
  }

  calcAxisStep(minValue: number, maxValue: number) {
    if (this.majorIncrement) {
      return this.majorIncrement;
    }

    const spread = maxValue - minValue;

    if (spread === 0) {
      if (minValue === 0) {
        return 10;
      }
      return Math.pow(10, Math.floor(Math.log10(minValue)) - 1);
    }

    if (this.isLog) {
      return Math.floor(spread / 8) + 1;
    }

    if (this.majorTickCount) {
      const increment = spread / this.majorTickCount;
      const testVal = Math.pow(10, Math.ceil(Math.log10(increment) - 1));
      let firstDigit = increment / testVal;
      if (firstDigit > 1 && firstDigit <= 1.5) {
        firstDigit = 1.5;
      } else if (firstDigit > 5) {
        firstDigit = 10;
      } else {
        firstDigit = Math.ceil(firstDigit);
      }
      return firstDigit * testVal;
    }
    return this.getDefaultAxisStep(spread);
  }

  /**
   * Aesthetically choose a step depending of the spread value.
   * @param spread The spread of the data values.
   * @returns The step for the axis.
   */
  getDefaultAxisStep(spread: number): number {
    const t = Math.log10(spread);
    const testVal = Math.pow(10, Math.ceil(t) - 2);
    const first2Digits = Math.round(spread / testVal);

    let scaleFactor = 1;
    if (first2Digits >= 10 && first2Digits <= 14) {
      scaleFactor = 2;
    } else if (first2Digits >= 15 && first2Digits <= 19) {
      scaleFactor = 3;
    } else if (first2Digits >= 20 && first2Digits <= 24) {
      scaleFactor = 4;
    } else if (first2Digits >= 25 && first2Digits <= 45) {
      scaleFactor = 5;
    } else if (first2Digits >= 46 && first2Digits <= 80) {
      scaleFactor = 10;
    } else {
      scaleFactor = 20;
    }
    return scaleFactor * testVal;
  }

  linearToActual(value: number) {
    return this.isLog ? Math.pow(10, value) : value;
  }

  actualToLinear(value?: number) {
    if (value == null) {
      return;
    }

    if (this.isLog) {
      return Math.log10(value);
    }
    return value;
  }

  getScale(range: [number, number]) {
    const { viewportMin, viewportMax } = this.getAxisViewport();
    const domain: [number, number] = [
      this.linearToActual(viewportMin),
      this.linearToActual(viewportMax)
    ];
    if (this.isLog) {
      return new ScaleLog(domain, range);
    }
    return new ScaleLinear(domain, range);
  }

  getFirstTick() {
    if (this.isZeroBaseline) {
      return Math.ceil(this.viewportMin! / this.majorIncrement!) * this.majorIncrement!;
    }
    return (
      this.min! +
      Math.ceil((this.viewportMin! - this.min!) / this.majorIncrement!) * this.majorIncrement!
    );
  }

  getAxisViewport() {
    return {
      viewportMin: this.viewportMin!,
      viewportMax: this.viewportMax!
    };
  }

  /**
   * Returns the min and max extent of the axis.
   */
  getAxisExtent() {
    return {
      min: this.min!,
      max: this.max!
    };
  }
  /**
   * Returns the major ticks.
   */
  getTicks() {
    let firstTick = this.getFirstTick();
    const ticks = [];

    while (firstTick <= this.viewportMax!) {
      ticks.push(this.linearToActual(firstTick));
      firstTick += this.majorIncrement!;
    }
    return ticks;
  }

  getMinorTicks() {
    const coords = [];
    for (let i = -1; i <= this.majorTickCount; i++) {
      const value = i * this.majorIncrement! + this.getFirstTick();
      if (this.isLog && this.majorIncrement == 1 && this.minorIncrement == 1) {
        // draw linear ticks from 2 to 9
        for (let j = 2; j <= 9; j++) {
          const linearValue = value + Math.log10(j);
          if (linearValue > this.max!) break;
          if (linearValue < this.min!) continue;
          coords.push(this.linearToActual(linearValue));
        }
      } else {
        for (let j = 1; j < this.minorTickCount; j++) {
          const minorValue = value + j * this.minorIncrement!;
          if (minorValue > this.max!) break;
          if (minorValue < this.min!) continue;

          coords.push(minorValue);
        }
      }
    }
    return coords;
  }

  getStep() {
    return this.majorIncrement!;
  }

  getMinExtent() {
    return (this.max! - this.min!) / MAX_ZOOM;
  }
}
